"use client";

import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

/** Shared pill size/styling for ICD chips and the add-code control. */
export const icdChipButtonClassName =
  "inline-flex shrink-0 items-center justify-center font-mono text-base font-semibold rounded-full px-2.5 py-0.5 bg-indigo-50 text-indigo-800 border border-indigo-200 hover:bg-indigo-100 transition-colors cursor-pointer";

export function Icd10AddCodeButton({ onClick, className = "" }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Add code"
            onClick={onClick}
            className={`${icdChipButtonClassName} ${className}`.trim()}
          >
            <Plus className="w-3 h-3" strokeWidth={2.5} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">Add code</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function Icd10CodeChips({
  codes = [],
  onCodeClick,
  className = "",
  readOnly = false,
  trailingAction = null,
}) {
  if (!codes.length && !trailingAction) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {codes.map((code) => (
        <button
          key={code}
          type="button"
          title={readOnly ? code : `Click to change ${code}`}
          onClick={() => onCodeClick?.(code)}
          className={`${icdChipButtonClassName} hover:ring-2 hover:ring-indigo-300/50`}
        >
          {code}
        </button>
      ))}
      {trailingAction}
    </div>
  );
}

export default function SoapIcd10ProblemListPanel({
  problemList = [],
  isReadOnly = false,
  isLoading = false,
  onOpenAddModal,
  onCodeEdit,
  // Soft delete: hidden codes stay in the SOAP note text, they are only
  // removed from this list and can be restored.
  hiddenProblems = [],
  onHideCode,
  onRestoreCode,
}) {
  const [showHidden, setShowHidden] = useState(false);
  // Code awaiting delete confirmation (null = dialog closed).
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await onHideCode?.(pendingDelete.code);
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };
  const canHide = !isReadOnly && typeof onHideCode === "function";
  const columns = ["#", "Code", "Description", "Source"];
  if (canHide) columns.push("");

  return (
    <div
      style={{
        background: "#fafcff",
        borderRadius: "20px",
        border: "1px solid #eef2f8",
        marginTop: "0.5rem",
        padding: "1rem clamp(0.75rem, 3vw, 1.5rem)",
      }}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#1f3b4c",
            margin: 0,
          }}
        >
          Problem List (ICD-10-CM)
        </h3>
        {!isReadOnly && <Icd10AddCodeButton onClick={onOpenAddModal} />}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-gray-600">Loading problem list…</span>
        </div>
      ) : problemList.length === 0 ? (
        <p className="text-base text-gray-500 py-4 text-center">
          {hiddenProblems.length > 0
            ? "All ICD-10 codes have been removed from this list (they remain in the SOAP note)."
            : "No ICD-10 codes yet."}{" "}
          {!isReadOnly && (
            <>
              Click the <strong>+</strong> button to search the database or enter
              a code manually.
            </>
          )}
        </p>
      ) : (
        <>
          {/* Always a real table (rows + columns). On narrow screens the table
              keeps a readable minimum width and scrolls sideways inside its own
              container instead of squeezing the columns. */}
          <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[32rem] border-collapse text-[1.0625rem]">
            <thead>
              <tr>
                {columns.map((h, i) => (
                  <th
                    key={`${h}-${i}`}
                    className="border-b border-slate-200 py-2 pr-1.5 text-left text-base font-semibold text-[#4b637a]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {problemList.map((problem, index) => (
                <tr key={problem.id || `${problem.code}-${index}`}>
                  <td className="whitespace-nowrap border-b border-[#edf2f8] py-2 pr-2 align-top">
                    <span
                      style={{
                        width: "26px",
                        height: "26px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#eef2ff",
                        borderRadius: "50%",
                        fontSize: "0.9375rem",
                        fontWeight: 600,
                        color: "#1f4f6e",
                      }}
                    >
                      {index + 1}
                    </span>
                  </td>
                  <td className="whitespace-nowrap border-b border-[#edf2f8] py-2 pr-2 align-top">
                    <button
                      type="button"
                      title={`Change ${problem.code}`}
                      onClick={() => onCodeEdit?.(problem.code)}
                      style={{
                        fontFamily: "monospace",
                        background: "#eef2ff",
                        padding: "2px 8px",
                        borderRadius: "20px",
                        fontSize: "1.0625rem",
                        fontWeight: 600,
                        color: "#1f4f6e",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      {problem.code}
                    </button>
                  </td>
                  <td className="break-words border-b border-[#edf2f8] py-2 pr-2 align-top text-lg leading-relaxed text-[#2c3f55]">
                    {problem.description || "—"}
                    {problem.descriptionSource === "gpt" && (
                      <Badge
                        variant="outline"
                        className="ml-2 text-base font-normal text-yellow-800 border-yellow-200 bg-yellow-50"
                      >
                        GPT
                      </Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap border-b border-[#edf2f8] py-2 pr-1 align-top">
                    <span
                      style={{
                        background:
                          problem.source === "precharting"
                            ? "#f0fdf4"
                            : problem.source === "manual"
                              ? "#eff6ff"
                              : "#f5f3ff",
                        color:
                          problem.source === "precharting"
                            ? "#15803d"
                            : problem.source === "manual"
                              ? "#1d4ed8"
                              : "#7e22ce",
                        padding: "2px 8px",
                        borderRadius: "30px",
                        fontSize: "1rem",
                        fontWeight: 500,
                      }}
                    >
                      {problem.source === "precharting"
                        ? "Precharting"
                        : problem.source === "manual"
                          ? "Added"
                          : "SOAP Note"}
                    </span>
                  </td>
                  {canHide && (
                    <td className="whitespace-nowrap border-b border-[#edf2f8] py-2 pl-1 text-right align-top">
                      <button
                        type="button"
                        aria-label={`Remove ${problem.code} from Problem List`}
                        title="Remove from Problem List (the code is retained in the SOAP note)"
                        onClick={() => setPendingDelete(problem)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-600 transition-colors hover:border-red-200 hover:bg-red-100 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p
            style={{
              marginTop: "12px",
              fontSize: "1rem",
              color: "#6f8fae",
            }}
          >
            Click a code to change it. Use the + button to search the database or
            enter a code manually.
            {canHide &&
              " Use the delete icon to remove a code from the Problem List. The code remains in the SOAP note and can be restored at any time."}
          </p>
        </>
      )}

      {hiddenProblems.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <button
            type="button"
            onClick={() => setShowHidden((prev) => !prev)}
            className="flex items-center gap-1.5 text-base font-medium text-[#4b637a] hover:text-[#1f3b4c]"
          >
            {showHidden ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
            Removed from list ({hiddenProblems.length})
          </button>
          {showHidden && (
            <ul className="mt-2 space-y-1.5">
              {hiddenProblems.map((problem, index) => (
                <li
                  key={problem.id || `${problem.code}-hidden-${index}`}
                  className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-1.5"
                >
                  <span className="min-w-0 break-words text-base text-slate-500">
                    <span className="mr-2 font-mono font-semibold text-slate-600 line-through">
                      {problem.code}
                    </span>
                    {problem.description || ""}
                  </span>
                  {!isReadOnly && typeof onRestoreCode === "function" && (
                    <button
                      type="button"
                      onClick={() => onRestoreCode(problem.code)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-sm font-medium text-indigo-800 hover:bg-indigo-100"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Restore
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Dialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setPendingDelete(null);
        }}
      >
        <DialogContent className="max-w-md w-[calc(100vw-2rem)]">
          <DialogHeader className="space-y-3 text-left">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                <Trash2 className="h-5 w-5" />
              </span>
              <DialogTitle className="text-xl font-semibold text-[#1f3b4c]">
                Remove diagnosis code from Problem List?
              </DialogTitle>
            </div>
            <DialogDescription asChild>
              <div className="space-y-3 text-left text-base text-slate-600">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="font-mono font-semibold text-[#1f4f6e]">
                    {pendingDelete?.code}
                  </span>
                  {pendingDelete?.description && (
                    <span className="text-slate-700">
                      {" "}
                      &mdash; {pendingDelete.description}
                    </span>
                  )}
                </div>
                <p>
                  This code will be removed from the Problem List only. The
                  Assessment &amp; Plan in the SOAP note will not be modified.
                </p>
                <p>
                  You can restore it at any time from the &ldquo;Removed from
                  list&rdquo; section below the table.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-300"
            >
              {isDeleting ? "Removing…" : "Yes, remove code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
